/**
 * PDF Styles Component
 * 
 * Injects CSS styles for PDF rendering into the document head.
 * Web-only - handles text layer, page layout, and selection styles.
 *
 * Matches Orbit demo aesthetics:
 * - Warm cream backgrounds (#F9F6F1)
 * - Right margin reserved for prompt rail
 * - Paper-like page styling with shadows
 * - Single scroll container (no nested scrollbars)
 */

import React, { useEffect } from "react";

const pdfStyles = `
/* === CSS Variables for Orbit PDF Reader === */
/* Matches orbit-summer-2022-demo index.css exactly */
:root {
  --pdfPromptRailWidth: 380px;

  /* Core palette - matches demo */
  --accentPrimary: #D60909;
  --accentSecondary: #A06813;
  --fgPrimary: rgba(0,0,0,0.8);
  --fgSecondaryLarge: #908672;
  --fgSecondarySmall: #826D4E;
  --fgTertiary: #E2DCD2;
  --fgDisabled: #AAAAAA;
  --bgPrimary: #F9F6F1;
  --bgSecondary: #eeeae2;
  --bgContent: #fdfcfb;
  --hoverLayer: rgba(92, 56, 0, 0.05);
  --pressedLayer: rgba(92, 56, 0, 0.14);
  --selection: #F9EBD9;
  --selectionHover: #D6090926;
  --editSelection: #76a7e8;
  --fadeTransition: opacity 0.1s linear;
  --expoTiming: cubic-bezier(0.16, 1, 0.3, 1);

  /* Aliased for PDF components */
  --pdfCreamBg: var(--bgSecondary);
  --pdfPaperBg: var(--bgPrimary);
  --pdfAccentPrimary: var(--accentPrimary);
  --pdfAccentSecondary: var(--accentSecondary);
  --pdfInk: var(--fgPrimary);
  --pdfInkSecondary: var(--fgSecondaryLarge);
  --pdfSelection: var(--selection);
  --pdfBorderColor: var(--fgTertiary);
}

/* === PDF Reader Root Layout === */

.pdfReaderRoot {
  min-height: 100vh;
  background: var(--pdfCreamBg);
  position: relative;
}

.pdfReaderReady {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* === PDF Header === */

.pdfHeader {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background: var(--pdfCreamBg);
  border-bottom: 1px solid var(--pdfBorderColor);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.pdfHeader--transparent {
  background: transparent;
  border-bottom: none;
}

/* === PDF Container & Pages === */

.pdfContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 48px 24px 120px 24px;
  min-height: calc(100vh - 60px);
  background: var(--pdfCreamBg);
  /* Center pages with room for prompts in right margin */
  max-width: calc(100vw - var(--pdfPromptRailWidth));
  margin: 0 auto;
}

/* When no prompt rail is needed (e.g., narrow screens) */
.pdfContainer--fullWidth {
  max-width: 100%;
  margin: 0 auto;
}

.pdfPage {
  position: relative;
  background: var(--pdfPaperBg);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.06),
    0 8px 24px var(--pdfShadowLight);
  border-radius: 2px;
}

.pdfPageBackground {
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

/* === Text Layer (Invisible but Selectable) === */

.pdfTextLayer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  /* Make text invisible but keep it selectable */
  color: transparent;
  user-select: text;
  -webkit-user-select: text;
  line-height: 1;
  white-space: pre;
}

.pdfTextLayer span {
  color: transparent;
  position: absolute;
  white-space: pre;
  transform-origin: left bottom;
  cursor: text;
}

/* OCR-specific text layer styling */
.pdfTextLayer.pdfTextLayer--ocr {
  /* Same as native, just a semantic marker */
}

.pdfTextLayer .ocrLine {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Empty text layer (no text available) */
.pdfTextLayer.pdfTextLayer--empty {
  pointer-events: none;
}

/* Selection highlighting - Orbit palette */
.pdfTextLayer ::selection {
  background: var(--pdfSelection);
  color: transparent;
}

.pdfTextLayer ::-moz-selection {
  background: var(--pdfSelection);
  color: transparent;
}

/* === Orbit Anchor Highlights (on PDF text) === */

.pdfTextLayer [class^="orbitanchor-"],
.pdfTextLayer [class*=" orbitanchor-"] {
  border-radius: 2px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  mix-blend-mode: multiply;
}

.orbit-anchor-highlight {
  border-radius: 2px;
  transition: background-color 0.15s ease;
}

/* === PDF Content Area === */

.pdfContent {
  position: relative;
  z-index: 0;
  flex: 1;
}

#pdf-content {
  position: relative;
  z-index: 0;
}

/* === Prompt Rail Overlay Layer === */

.pdfPromptRail {
  position: fixed;
  top: 0;
  right: 0;
  width: var(--pdfPromptRailWidth);
  height: 100vh;
  pointer-events: none;
  z-index: 50;
  overflow: visible;
}

.pdfPromptRail > * {
  pointer-events: auto;
}

/* === Orbit Menu Container === */

.pdfOrbitMenuContainer {
  position: fixed;
  bottom: 40px;
  right: 48px;
  z-index: 200;
  pointer-events: none;
}

.pdfOrbitMenuContainer > * {
  pointer-events: auto;
}

/* === Upload UI === */

.pdfUploadContainer,
.pdfLoadingContainer,
.pdfErrorContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px;
  background: var(--pdfCreamBg);
}

.pdfUploadBox,
.pdfLoadingBox,
.pdfErrorBox {
  background: var(--pdfPaperBg);
  border-radius: 16px;
  padding: 56px 72px;
  text-align: center;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.06),
    0 8px 32px var(--pdfShadowMedium);
  max-width: 520px;
  border: 3px solid var(--pdfBorderColor);
}

.pdfUploadBox h2,
.pdfLoadingBox h2,
.pdfErrorBox h2 {
  font-family: "Dr-ExtraBold", "dr-extrabold", system-ui, sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: var(--pdfInk);
  margin: 0 0 16px 0;
  letter-spacing: 0.02em;
}

.pdfUploadBox p,
.pdfLoadingBox p,
.pdfErrorBox p {
  font-family: "Dr-Medium", "dr-regular", system-ui, sans-serif;
  font-size: 15px;
  color: var(--pdfInkSecondary);
  margin: 0 0 28px 0;
  line-height: 1.6;
}

.pdfUploadBox--ocr {
  border: 3px dashed var(--pdfAccentSecondary);
}

/* Orbit-style file input button */
.pdfFileInput {
  display: block;
  margin: 0 auto;
  padding: 14px 32px;
  font-family: "Dr-Bold", "dr-medium", system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: white;
  background: var(--pdfAccentPrimary);
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow:
    0 2px 8px rgba(237, 55, 73, 0.25),
    0 1px 2px rgba(0, 0, 0, 0.1);
}

.pdfFileInput:hover {
  background: #d63344;
  transform: translateY(-1px);
  box-shadow:
    0 4px 12px rgba(237, 55, 73, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.1);
}

.pdfFileInput:active {
  transform: translateY(0);
  background: #c52d3d;
}

.pdfFileInput:focus {
  outline: none;
  box-shadow:
    0 0 0 4px rgba(237, 55, 73, 0.2),
    0 2px 8px rgba(237, 55, 73, 0.25);
}

/* Secondary style for OCR upload */
.pdfUploadBox--ocr .pdfFileInput {
  background: var(--pdfAccentSecondary);
  box-shadow:
    0 2px 8px rgba(160, 104, 19, 0.25),
    0 1px 2px rgba(0, 0, 0, 0.1);
}

.pdfUploadBox--ocr .pdfFileInput:hover {
  background: #8a5a10;
}

/* === Progress Bar === */

.pdfProgressBar {
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 20px;
}

.pdfProgressFill {
  height: 100%;
  background: var(--pdfAccentPrimary);
  border-radius: 3px;
  transition: width 0.25s var(--expoTiming);
}

/* Indeterminate progress bar animation */
.pdfProgressBar--indeterminate {
  position: relative;
}

.pdfProgressFill--animated {
  width: 30%;
  animation: indeterminate 1.5s ease-in-out infinite;
}

@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(233%);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* OCR-specific loading styles */
.pdfLoadingBox--ocr {
  border-color: var(--pdfAccentSecondary);
}

.pdfLoadingBox--ocr .pdfProgressFill {
  background: var(--pdfAccentSecondary);
}

.pdfOcrProgress {
  font-family: "Dr-Medium", system-ui, sans-serif;
  font-size: 13px;
  color: var(--pdfInkSecondary);
  font-style: italic;
}

/* === Error State === */

.pdfErrorBox h2 {
  color: var(--pdfAccentPrimary);
}

.pdfErrorBox button {
  padding: 14px 32px;
  font-family: "Dr-Bold", system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: white;
  background: var(--pdfAccentPrimary);
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow:
    0 2px 8px rgba(237, 55, 73, 0.25),
    0 1px 2px rgba(0, 0, 0, 0.1);
}

.pdfErrorBox button:hover {
  background: #d63344;
  transform: translateY(-1px);
}

/* === RTL Support for OCR === */

.pdfTextLayer[dir="rtl"] {
  direction: rtl;
  text-align: right;
}

.pdfTextLayer[dir="rtl"] .ocrLine {
  direction: rtl;
}

/* === Contextual Menu Styles === */

.pdfContextualMenu {
  position: fixed;
  display: flex;
  flex-direction: column;
  background: var(--pdfPaperBg);
  border-radius: 8px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
  min-width: 140px;
}

.pdfContextualMenuItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-family: "Dr-Medium", system-ui, sans-serif;
  font-size: 14px;
  color: var(--pdfInk);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.1s ease;
  text-align: left;
}

.pdfContextualMenuItem:hover {
  background: rgba(0, 0, 0, 0.04);
}

.pdfContextualMenuItem:active {
  background: rgba(0, 0, 0, 0.08);
}

.pdfContextualMenuShortcut {
  font-size: 12px;
  color: var(--pdfInkSecondary);
  margin-left: 16px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
}

/* === Prompt Card Styles === */

.pdfPromptCard {
  position: absolute;
  width: 332px;
  background: var(--pdfPaperBg);
  border-radius: 12px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 4px 16px var(--pdfShadowLight);
  border: 2px solid var(--pdfBorderColor);
  overflow: hidden;
  transition: transform 0.2s var(--expoTiming), box-shadow 0.2s ease;
}

.pdfPromptCard:hover {
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 8px 24px var(--pdfShadowMedium);
}

.pdfPromptCard--draft {
  border-color: var(--pdfAccentPrimary);
}

.pdfPromptCard--collapsed {
  cursor: pointer;
}

.pdfPromptCardHeader {
  padding: 12px 16px;
  border-bottom: 1px solid var(--pdfBorderColor);
  background: rgba(0, 0, 0, 0.02);
}

.pdfPromptCardSelectedText {
  font-family: "Dr-Medium", system-ui, sans-serif;
  font-size: 13px;
  color: var(--pdfInkSecondary);
  line-height: 1.4;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.pdfPromptCardBody {
  padding: 16px;
}

.pdfPromptCardLabel {
  font-family: "Dr-Bold", system-ui, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--pdfInkSecondary);
  margin-bottom: 6px;
}

.pdfPromptCardQuestion,
.pdfPromptCardAnswer {
  font-family: "Dr-Medium", system-ui, sans-serif;
  font-size: 15px;
  color: var(--pdfInk);
  line-height: 1.5;
  margin-bottom: 16px;
}

.pdfPromptCardQuestion:last-child,
.pdfPromptCardAnswer:last-child {
  margin-bottom: 0;
}

.pdfPromptCardTextarea {
  width: 100%;
  padding: 10px 12px;
  font-family: "Dr-Medium", system-ui, sans-serif;
  font-size: 14px;
  color: var(--pdfInk);
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid var(--pdfBorderColor);
  border-radius: 6px;
  resize: vertical;
  min-height: 60px;
  margin-bottom: 12px;
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.pdfPromptCardTextarea:focus {
  border-color: var(--pdfAccentPrimary);
  background: var(--pdfPaperBg);
}

.pdfPromptCardTextarea::placeholder {
  color: var(--pdfInkSecondary);
  opacity: 0.7;
}

.pdfPromptCardActions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid var(--pdfBorderColor);
  background: rgba(0, 0, 0, 0.02);
}

.pdfPromptCardButton {
  padding: 8px 16px;
  font-family: "Dr-Bold", system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.pdfPromptCardButton--secondary {
  background: transparent;
  color: var(--pdfInkSecondary);
  border: 1px solid var(--pdfBorderColor);
}

.pdfPromptCardButton--secondary:hover {
  background: rgba(0, 0, 0, 0.04);
}

.pdfPromptCardButton--primary {
  background: var(--pdfAccentPrimary);
  color: white;
  border: none;
}

.pdfPromptCardButton--primary:hover {
  background: #d63344;
}

.pdfPromptCardButton--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* === Responsive adjustments === */

@media (max-width: 900px) {
  :root {
    --pdfPromptRailWidth: 0px;
  }

  .pdfContainer {
    max-width: 100%;
    margin: 0 auto;
    padding: 24px 16px 120px 16px;
  }

  .pdfPromptRail {
    display: none;
  }

  .pdfPromptCard {
    position: fixed;
    right: 16px;
    width: calc(100vw - 32px);
    max-width: 400px;
  }
}

@media (max-width: 600px) {
  .pdfContainer {
    padding: 16px 8px 100px 8px;
    gap: 16px;
  }

  .pdfUploadBox,
  .pdfLoadingBox,
  .pdfErrorBox {
    padding: 32px 24px;
    margin: 0 16px;
  }
}

/* === Orbit Prompt Rail (New Design) === */

.orbitPromptRail {
  position: fixed;
  top: 80px;
  right: 24px;
  width: 340px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 0 0 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 100;
  scrollbar-width: thin;
  scrollbar-color: var(--fgTertiary) transparent;
}

.orbitPromptRail::-webkit-scrollbar {
  width: 6px;
}

.orbitPromptRail::-webkit-scrollbar-track {
  background: transparent;
}

.orbitPromptRail::-webkit-scrollbar-thumb {
  background: var(--fgTertiary);
  border-radius: 3px;
}

/* New Prompt Button */
.orbitNewPromptBtn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: var(--bgPrimary);
  border: 2px dashed var(--fgTertiary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Dr-Bold", Georgia, serif;
  font-size: 14px;
  color: var(--fgSecondaryLarge);
  letter-spacing: 0.01em;
}

.orbitNewPromptBtn:hover {
  border-color: var(--accentPrimary);
  background: var(--bgContent);
  color: var(--accentPrimary);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(214, 9, 9, 0.1);
}

.orbitNewPromptIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--bgSecondary);
  border-radius: 6px;
  font-size: 18px;
  font-weight: 300;
  transition: all 0.2s ease;
}

.orbitNewPromptBtn:hover .orbitNewPromptIcon {
  background: var(--accentPrimary);
  color: white;
}

/* Prompt Card */
.orbitPromptCard {
  background: var(--bgPrimary);
  border-radius: 12px;
  border: 1px solid var(--fgTertiary);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 4px 12px rgba(77, 51, 8, 0.06);
}

.orbitPromptCard:hover {
  border-color: var(--accentSecondary);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(77, 51, 8, 0.1);
  transform: translateY(-2px);
}

.orbitPromptCard--editing {
  border: 2px solid var(--accentPrimary);
  cursor: default;
  box-shadow:
    0 4px 16px rgba(214, 9, 9, 0.12),
    0 8px 32px rgba(77, 51, 8, 0.08);
}

.orbitPromptCard--editing:hover {
  transform: none;
}

.orbitPromptCard--expanded {
  border-color: var(--accentSecondary);
}

.orbitPromptCardInner {
  padding: 16px 18px;
}

/* Prompt Field (for editing) */
.orbitPromptField {
  margin-bottom: 14px;
}

.orbitPromptField:last-of-type {
  margin-bottom: 0;
}

.orbitPromptLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Dr-Bold", Georgia, serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--fgSecondaryLarge);
  margin-bottom: 8px;
}

.orbitPromptLabelIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: var(--accentPrimary);
  color: white;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

.orbitPromptInput {
  width: 100%;
  padding: 12px 14px;
  font-family: "Dr-Medium", Georgia, serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--fgPrimary);
  background: var(--bgContent);
  border: 1px solid var(--fgTertiary);
  border-radius: 8px;
  resize: none;
  outline: none;
  transition: all 0.15s ease;
}

.orbitPromptInput:focus {
  border-color: var(--accentPrimary);
  background: white;
  box-shadow: 0 0 0 3px rgba(214, 9, 9, 0.08);
}

.orbitPromptInput::placeholder {
  color: var(--fgSecondaryLarge);
  opacity: 0.7;
}

/* Prompt Actions */
.orbitPromptActions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--fgTertiary);
}

.orbitPromptBtn {
  padding: 10px 18px;
  font-family: "Dr-Bold", Georgia, serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.orbitPromptBtn--ghost {
  background: transparent;
  color: var(--fgSecondaryLarge);
  border: 1px solid var(--fgTertiary);
}

.orbitPromptBtn--ghost:hover {
  background: var(--bgSecondary);
  border-color: var(--fgSecondaryLarge);
}

.orbitPromptBtn--primary {
  background: var(--accentPrimary);
  color: white;
  border: none;
}

.orbitPromptBtn--primary:hover {
  background: #c00808;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(214, 9, 9, 0.25);
}

.orbitPromptBtn--primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Saved Prompt Display */
.orbitPromptQuestion {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.orbitPromptQIcon,
.orbitPromptAIcon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  font-family: "Dr-Bold", Georgia, serif;
  font-size: 11px;
  font-weight: 700;
  margin-top: 2px;
}

.orbitPromptQIcon {
  background: var(--accentPrimary);
  color: white;
}

.orbitPromptAIcon {
  background: var(--accentSecondary);
  color: white;
}

.orbitPromptQText,
.orbitPromptAText {
  font-family: "Dr-Medium", Georgia, serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--fgPrimary);
  flex: 1;
}

.orbitPromptAnswer {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--fgTertiary);
}

.orbitPromptHint {
  margin-top: 10px;
  font-family: "Dr-Medium", Georgia, serif;
  font-size: 12px;
  color: var(--fgSecondaryLarge);
  opacity: 0.7;
  font-style: italic;
}

/* Empty State */
.orbitEmptyState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
}

.orbitEmptyIcon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bgSecondary);
  border-radius: 50%;
  color: var(--fgSecondaryLarge);
  margin-bottom: 16px;
}

.orbitEmptyText {
  font-family: "Dr-Medium", Georgia, serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--fgSecondaryLarge);
  max-width: 240px;
}

/* Responsive */
@media (max-width: 1200px) {
  .orbitPromptRail {
    width: 300px;
    right: 16px;
  }
}

@media (max-width: 900px) {
  .orbitPromptRail {
    display: none;
  }
}

/* === Inline Prompt Box Styles (Orbit Demo Style) === */

.inlinePromptBox [data-placeholder]:empty::before {
  content: attr(data-placeholder);
  color: var(--fgDisabled);
  pointer-events: none;
}

/* Context menu placeholder styles */
.pdfContextMenu {
  /* Styles are inline but this ensures it's recognized */
}

/* === Translation Overlay Styles === */
/*
 * Three-column translation layout:
 * [Arabic Panel] --- [PDF Page with Highlights] --- [English Translation]
 *     LEFT                    CENTER                      RIGHT
 *
 * Visual design principles (Orbit / Andy Matuschak aesthetic):
 * - Clean, minimal with generous whitespace
 * - Warm pastel color palette linking Arabic ↔ Highlight ↔ English
 * - Thoughtful typography: Scheherazade New for Arabic, Dr fonts for English
 * - Subtle hover interactions with smooth transitions
 * - Selectable Arabic text for copying (unlike scanned PDF)
 */

.translationOverlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 90;
  overflow: visible;
}

.translationOverlay > * {
  pointer-events: auto;
}

/* Right-side English translation blobs */
.translationBlob {
  font-family: "Dr-Medium", Georgia, serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--fgPrimary);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.translationBlob:hover {
  transform: translateX(4px);
}

/* Left-side Arabic text panels */
.translationArabicBlob {
  font-family: "Scheherazade New", serif;
  font-size: 20px;
  line-height: 1.9;
  direction: rtl;
  text-align: right;
  color: var(--fgPrimary);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  /* Allow text selection for copying Arabic */
  user-select: text;
  -webkit-user-select: text;
}

.translationArabicBlob:hover {
  transform: translateX(-4px);
}

/* Ensure Arabic text renders crisply */
.translationArabicBlob p {
  margin: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Paragraph highlight overlays on PDF */
.paragraphHighlight {
  mix-blend-mode: multiply;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

/* Connection lines (bezier curves) */
.connectionLine {
  pointer-events: none;
  overflow: visible;
}

/* Hide Arabic panel on narrow screens where there's no room */
@media (max-width: 1100px) {
  .translationArabicBlob {
    display: none;
  }
}

/* === Print styles === */

@media print {
  .pdfContainer {
    background: white;
    padding: 0;
    gap: 0;
    max-width: 100%;
  }

  .pdfPage {
    box-shadow: none;
    page-break-after: always;
  }

  .pdfTextLayer,
  .pdfPromptRail,
  .pdfOrbitMenuContainer,
  .pdfHeader {
    display: none;
  }
}
`;

let stylesInjected = false;

export function PdfStylesProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof document === "undefined" || stylesInjected) return;

    const styleEl = document.createElement("style");
    styleEl.id = "orbit-pdf-styles";
    styleEl.textContent = pdfStyles;
    document.head.appendChild(styleEl);
    stylesInjected = true;

    return () => {
      // Don't remove on unmount since other components might need it
    };
  }, []);

  return <>{children}</>;
}

export function usePdfStyles() {
  useEffect(() => {
    if (typeof document === "undefined" || stylesInjected) return;

    const styleEl = document.createElement("style");
    styleEl.id = "orbit-pdf-styles";
    styleEl.textContent = pdfStyles;
    document.head.appendChild(styleEl);
    stylesInjected = true;
  }, []);
}
